import * as THREE from "three";
import { dom } from "../dom";
import { state } from "../state";
import { renderer } from "../viewer/scene";
import { getActiveFragmentsModel, updateFragmentsView } from "../fragments/engine";
import { trackTool } from "../analytics/events";

const planeX = new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0);
const planeY = new THREE.Plane(new THREE.Vector3(0, -1, 0), 0);
const planeZ = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
const planPlane = new THREE.Plane();

function applyPlanClipping(): void {
  const axis = state.planView.cutAxis;
  const normal =
    axis === 0 ? new THREE.Vector3(-1, 0, 0) : axis === 2 ? new THREE.Vector3(0, 0, -1) : new THREE.Vector3(0, -1, 0);
  planPlane.set(normal, state.planView.cut);
  renderer.localClippingEnabled = false;
  renderer.clippingPlanes = [planPlane];
  updateClippingUi();
  updateFragmentsView(true);
}

/**
 * Применяет clipping planes к WebGLRenderer и активной модели:
 *   - axis.enabled === false → плоскость не используется.
 *   - axis.value ∈ [0, 1] — доля по габариту модели вдоль оси.
 *   - invert меняет направление нормали (отсечение «по другую сторону»).
 *
 * Если разрезы выключены или модель не загружена, clipping снимается.
 */
export function applyClipping(): void {
  if (state.planView.active) {
    applyPlanClipping();
    return;
  }
  if (!state.modelBounds || !state.clipping.enabled) {
    renderer.localClippingEnabled = false;
    renderer.clippingPlanes = [];
    updateClippingUi();
    // Заставляем Fragments перечитать плоскости (через getClippingPlanesEvent).
    updateFragmentsView(true);
    return;
  }

  const { min, max } = state.modelBounds;
  const sizeX = max[0] - min[0];
  const sizeY = max[1] - min[1];
  const sizeZ = max[2] - min[2];
  const eps = 0.0001;

  const planes: THREE.Plane[] = [];
  const { axisX, axisY, axisZ, invertX, invertY, invertZ } = state.clipping;
  if (axisX.enabled) {
    const cut = min[0] + sizeX * THREE.MathUtils.clamp(axisX.value, 0, 1);
    const normalSign = invertX ? 1 : -1;
    planeX.set(new THREE.Vector3(normalSign, 0, 0), -normalSign * cut + eps);
    planes.push(planeX);
  }
  if (axisY.enabled) {
    const cut = min[1] + sizeY * THREE.MathUtils.clamp(axisY.value, 0, 1);
    const normalSign = invertY ? 1 : -1;
    planeY.set(new THREE.Vector3(0, normalSign, 0), -normalSign * cut + eps);
    planes.push(planeY);
  }
  if (axisZ.enabled) {
    const cut = min[2] + sizeZ * THREE.MathUtils.clamp(axisZ.value, 0, 1);
    const normalSign = invertZ ? 1 : -1;
    planeZ.set(new THREE.Vector3(0, 0, normalSign), -normalSign * cut + eps);
    planes.push(planeZ);
  }

  renderer.localClippingEnabled = false;
  renderer.clippingPlanes = planes;
  updateClippingUi();
  // Fragments-меши отсекаются во воркере — форсим пересчёт после смены плоскостей.
  updateFragmentsView(true);
}

export function captureModelBounds(): void {
  if (!state.ifcModel) {
    state.modelBounds = null;
    return;
  }
  // У Fragments-модели геометрия живёт во воркере, поэтому Box3.setFromObject по
  // graph-объекту даёт пустой bbox (0/0/0). Берём готовый bbox из model.box.
  let bbox: THREE.Box3 | null = null;
  const model = state.fragmentsModelId ? getActiveFragmentsModel(state.fragmentsModelId) : undefined;
  if (model) {
    try {
      bbox = model.box;
    } catch {
      bbox = null;
    }
  }
  if (!bbox || bbox.isEmpty()) {
    bbox = new THREE.Box3().setFromObject(state.ifcModel);
  }
  if (bbox.isEmpty()) {
    state.modelBounds = null;
    return;
  }
  state.modelBounds = {
    min: [bbox.min.x, bbox.min.y, bbox.min.z],
    max: [bbox.max.x, bbox.max.y, bbox.max.z],
  };
}

export function resetClippingState(): void {
  state.clipping = {
    enabled: false,
    axisX: { enabled: false, value: 0.5 },
    axisY: { enabled: false, value: 0.5 },
    axisZ: { enabled: false, value: 0.5 },
    invertX: false,
    invertY: false,
    invertZ: false,
  };
  applyClipping();
  syncClippingUiFromState();
}

export function updateClippingUi(): void {
  const locked = state.planView.active;
  const hasModel = Boolean(state.ifcModel) && !locked;
  dom.clipEnabledCheckbox.disabled = !hasModel;
  dom.clipResetButton.disabled = !hasModel;
  dom.clipGrid.classList.toggle("is-locked", locked);
  dom.clipEnabledCheckbox.closest(".clip-toggle-row")?.classList.toggle("is-locked", locked);
  dom.clipPlanLock.hidden = !locked;
  for (const axis of ["x", "y", "z"] as const) {
    const checkbox = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-axis="${axis}"]`,
    );
    const range = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-range="${axis}"]`,
    );
    const invert = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-invert="${axis}"]`,
    );
    if (!checkbox || !range || !invert) {
      continue;
    }
    const enabledOverall = hasModel && state.clipping.enabled;
    checkbox.disabled = !enabledOverall;
    invert.disabled = !enabledOverall;
    const axisState =
      axis === "x" ? state.clipping.axisX : axis === "y" ? state.clipping.axisY : state.clipping.axisZ;
    range.disabled = !enabledOverall || !axisState.enabled;
  }
}

export function syncClippingUiFromState(): void {
  dom.clipEnabledCheckbox.checked = state.clipping.enabled;
  for (const axis of ["x", "y", "z"] as const) {
    const axisState =
      axis === "x" ? state.clipping.axisX : axis === "y" ? state.clipping.axisY : state.clipping.axisZ;
    const checkbox = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-axis="${axis}"]`,
    );
    const range = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-range="${axis}"]`,
    );
    const invert = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-invert="${axis}"]`,
    );
    if (checkbox) {
      checkbox.checked = axisState.enabled;
    }
    if (range) {
      range.value = String(axisState.value);
    }
    if (invert) {
      const inv =
        axis === "x"
          ? state.clipping.invertX
          : axis === "y"
            ? state.clipping.invertY
            : state.clipping.invertZ;
      invert.checked = inv;
    }
  }
  updateClippingUi();
}

export function initClippingControls(): void {
  syncClippingUiFromState();

  dom.clipEnabledCheckbox.addEventListener("change", () => {
    if (state.planView.active) {
      syncClippingUiFromState();
      return;
    }
    state.clipping.enabled = dom.clipEnabledCheckbox.checked;
    if (state.clipping.enabled) trackTool("clip");
    applyClipping();
  });

  dom.clipResetButton.addEventListener("click", () => {
    if (state.planView.active) {
      return;
    }
    resetClippingState();
  });

  for (const axis of ["x", "y", "z"] as const) {
    const checkbox = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-axis="${axis}"]`,
    );
    const range = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-range="${axis}"]`,
    );
    const invert = dom.clipGrid.querySelector<HTMLInputElement>(
      `input[data-clip-invert="${axis}"]`,
    );
    if (!checkbox || !range || !invert) {
      continue;
    }
    checkbox.addEventListener("change", () => {
      if (state.planView.active) {
        syncClippingUiFromState();
        return;
      }
      const axisState = axisRef(axis);
      axisState.enabled = checkbox.checked;
      applyClipping();
    });
    range.addEventListener("input", () => {
      if (state.planView.active) {
        syncClippingUiFromState();
        return;
      }
      const axisState = axisRef(axis);
      const v = Number(range.value);
      if (Number.isFinite(v)) {
        axisState.value = v;
        applyClipping();
      }
    });
    invert.addEventListener("change", () => {
      if (state.planView.active) {
        syncClippingUiFromState();
        return;
      }
      if (axis === "x") state.clipping.invertX = invert.checked;
      if (axis === "y") state.clipping.invertY = invert.checked;
      if (axis === "z") state.clipping.invertZ = invert.checked;
      applyClipping();
    });
  }
}

function axisRef(axis: "x" | "y" | "z") {
  return axis === "x"
    ? state.clipping.axisX
    : axis === "y"
      ? state.clipping.axisY
      : state.clipping.axisZ;
}
